import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  tsListTools, tsStats, tsListMachines, tsListVendors,
  tsCreateMachine, tsCreateVendor, tsCreateTool, tsPreviewFunctionalId,
  tsIssueTool, tsReturnTool,
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Wrench, Plus, Search, ScanLine, Package, ArrowDownRight, ArrowUpRight,
  Zap, AlertTriangle, Factory, Store, RefreshCw, ChevronRight,
} from 'lucide-react';

const CATEGORIES = [
  { code: 'punch', label: 'Punch', color: '#1a56db' },
  { code: 'die', label: 'Die', color: '#0D1F6E' },
  { code: 'ko_pin', label: 'KO Pin', color: '#0F6E56' },
  { code: 'collet', label: 'Collet / Sleeve', color: '#534AB7' },
  { code: 'gripper', label: 'Gripper / Header Die', color: '#d97706' },
  { code: 'trimmer', label: 'Trimmer', color: '#065F46' },
  { code: 'filler', label: 'Filler / Spacer', color: '#ea580c' },
  { code: 'other', label: 'Other', color: '#6B7280' },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.code, c]));

const MATERIALS = [
  'Tungsten Carbide', 'HSS M2', 'HSS M35', 'D2', 'D3', 'H13', 'H11', 'Other',
];

const STATUS_COLORS = {
  in_store: '#0e7a45',
  issued: '#d97706',
  in_tool_room: '#1a56db',
  at_external: '#7c3aed',
  pending_scrap: '#dc2626',
  scrapped: '#6b7280',
  converted: '#6b7280',
};

// ============ Register Tool Dialog ============
function RegisterToolDialog({ open, onOpenChange, machines, vendors, onCreated, mode = 'full' }) {
  const isQuick = mode === 'quick';
  const [form, setForm] = useState({});
  const [functionalIdPreview, setFunctionalIdPreview] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        item_type: 'finished_tool',
        current_stock: 1,
        machine_compatibility: [],
        is_critical: false,
        quick_register: isQuick,
      });
      setFunctionalIdPreview('');
    }
  }, [open, isQuick]);

  // Live preview of Functional ID
  useEffect(() => {
    const doPreview = async () => {
      const machine = (form.machine_compatibility || [])[0];
      const partNumber = form.part_number;
      const category = form.category;
      const station = form.station_number;
      if (!machine && !partNumber && !category && !station) {
        setFunctionalIdPreview('');
        return;
      }
      try {
        const res = await tsPreviewFunctionalId({ machine, part_number: partNumber, category, station });
        setFunctionalIdPreview(res.data.functional_id);
      } catch { /* silent */ }
    };
    const t = setTimeout(doPreview, 300);
    return () => clearTimeout(t);
  }, [form.machine_compatibility, form.part_number, form.category, form.station_number]);

  const handleSave = async () => {
    if (!form.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    if (isQuick && !form.category) {
      toast.error('Category is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.reference_cost) payload.reference_cost = parseFloat(payload.reference_cost);
      if (payload.current_stock) payload.current_stock = parseInt(payload.current_stock, 10);
      if (payload.min_level) payload.min_level = parseInt(payload.min_level, 10);
      if (payload.station_number) payload.station_number = String(payload.station_number);
      if (functionalIdPreview && !functionalIdPreview.includes('...')) {
        payload.functional_id = functionalIdPreview;
      }
      const res = await tsCreateTool(payload);
      toast.success(`${res.data.tl_number} registered${isQuick ? ' (Quick — complete within 24h)' : ''}`);
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register tool');
    }
    setSaving(false);
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={isQuick ? 'quick-register-dialog' : 'register-tool-dialog'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {isQuick ? <><Zap size={16} className="text-amber-500" /> Quick Register — 60 second entry</>
              : <><Wrench size={16} className="text-[#1a2fce]" /> Register New Tool</>}
          </DialogTitle>
        </DialogHeader>

        {/* Live Functional ID preview */}
        {(functionalIdPreview && functionalIdPreview !== '...') && (
          <div className="bg-[#1a2fce]/5 border border-[#1a2fce]/20 rounded-lg p-2 -mt-1" data-testid="functional-id-preview">
            <div className="text-[10px] uppercase text-gray-500">Functional ID (auto-building)</div>
            <div className="font-mono font-bold text-sm text-[#0d1f6e]">{functionalIdPreview}</div>
          </div>
        )}

        <div className="space-y-3">
          {/* Item type */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Item Type</Label>
              <Select value={form.item_type} onValueChange={(v) => setField('item_type', v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="field-item-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="finished_tool">Finished Tool</SelectItem>
                  <SelectItem value="blank">Blank / Semi-finished</SelectItem>
                  <SelectItem value="raw_material">Raw Material</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setField('category', v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="field-category"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={!!form.is_critical} onChange={(e) => setField('is_critical', e.target.checked)} data-testid="field-critical" />
                <span>Critical tool (cert mandatory)</span>
              </label>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs">Description *</Label>
            <Input
              autoFocus
              value={form.description || ''}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="e.g. M10 First Punch — Carbide YG8"
              className="h-9 text-sm"
              data-testid="field-description"
            />
          </div>

          {/* Machine + station + part */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Machine</Label>
              <Select value={(form.machine_compatibility || [])[0] || ''} onValueChange={(v) => setField('machine_compatibility', v ? [v] : [])}>
                <SelectTrigger className="h-9 text-xs" data-testid="field-machine"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {machines.map((m) => <SelectItem key={m.code} value={m.code}>{m.code} — {m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Part Number</Label>
              <Input value={form.part_number || ''} onChange={(e) => setField('part_number', e.target.value.toUpperCase())} placeholder="TAA124AW1Z1" className="h-9 text-xs font-mono" />
            </div>
            <div>
              <Label className="text-xs">Station</Label>
              <Input value={form.station_number || ''} onChange={(e) => setField('station_number', e.target.value)} placeholder="e.g. 2 or S2" className="h-9 text-xs" data-testid="field-station" />
            </div>
          </div>

          {/* Size + drawing + engraved */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Size</Label>
              <Input value={form.size || ''} onChange={(e) => setField('size', e.target.value)} placeholder="M10 × 45mm" className="h-9 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Drawing No.</Label>
              <Input value={form.drawing_number || ''} onChange={(e) => setField('drawing_number', e.target.value)} placeholder="DRG-2024-089" className="h-9 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Existing Engraved No.</Label>
              <Input value={form.existing_engraved_number || ''} onChange={(e) => setField('existing_engraved_number', e.target.value)} placeholder="Die-103" className="h-9 text-xs" />
            </div>
          </div>

          {/* Material + vendor + cert */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Material</Label>
              <Select value={form.material_type} onValueChange={(v) => setField('material_type', v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Carbide Grade</Label>
              <Input value={form.carbide_grade || ''} onChange={(e) => setField('carbide_grade', e.target.value)} placeholder="YG8" className="h-9 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Vendor</Label>
              <Select value={form.vendor} onValueChange={(v) => setField('vendor', v)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Material Cert No.{form.is_critical && ' *'}</Label>
              <Input value={form.material_cert_number || ''} onChange={(e) => setField('material_cert_number', e.target.value)} placeholder="CERT-…" className="h-9 text-xs font-mono" />
            </div>
            <div>
              <Label className="text-xs">Heat No.</Label>
              <Input value={form.heat_number || ''} onChange={(e) => setField('heat_number', e.target.value)} placeholder="HT-…" className="h-9 text-xs font-mono" />
            </div>
            <div>
              <Label className="text-xs">Reference Cost (₹)</Label>
              <Input type="number" value={form.reference_cost || ''} onChange={(e) => setField('reference_cost', e.target.value)} placeholder="2450" className="h-9 text-xs" />
            </div>
          </div>

          {/* Stock + location */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Current Stock *</Label>
              <Input type="number" value={form.current_stock ?? 1} onChange={(e) => setField('current_stock', e.target.value)} className="h-9 text-xs" data-testid="field-stock" />
            </div>
            <div>
              <Label className="text-xs">Min Level (reorder alert)</Label>
              <Input type="number" value={form.min_level || ''} onChange={(e) => setField('min_level', e.target.value)} className="h-9 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Bin Location *</Label>
              <Input value={form.bin_location || ''} onChange={(e) => setField('bin_location', e.target.value)} placeholder="RACK-26·SHELF-C·BIN-3" className="h-9 text-xs font-mono" data-testid="field-bin" />
            </div>
          </div>

          {!isQuick && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-500">Life tracking (all optional)</summary>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                <div><Label className="text-[10px]">Original length (mm)</Label><Input type="number" value={form.original_length_mm || ''} onChange={(e) => setField('original_length_mm', parseFloat(e.target.value) || null)} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Current length (mm)</Label><Input type="number" value={form.current_length_mm || ''} onChange={(e) => setField('current_length_mm', parseFloat(e.target.value) || null)} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Min serviceable (mm)</Label><Input type="number" value={form.minimum_serviceable_length_mm || ''} onChange={(e) => setField('minimum_serviceable_length_mm', parseFloat(e.target.value) || null)} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">Max regrind limit</Label><Input type="number" value={form.max_regrind_limit || ''} onChange={(e) => setField('max_regrind_limit', parseInt(e.target.value) || null)} className="h-8 text-xs" /></div>
              </div>
            </details>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="gap-1"
            style={{ backgroundColor: '#1a2fce' }}
            data-testid="submit-register-tool"
          >
            <Plus size={14} />
            {saving ? 'Saving…' : (isQuick ? 'Quick Register & Assign TL' : 'Register Tool')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ Issue / Return Dialog ============
function IssueReturnDialog({ open, onOpenChange, tool, mode, machines, onDone }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({}); }, [open]);

  if (!tool) return null;

  const isIssue = mode === 'issue';

  const submit = async () => {
    setSaving(true);
    try {
      if (isIssue) {
        const res = await tsIssueTool(tool.id, {
          machine: form.machine,
          operator: form.operator,
          job_card_ref: form.job_card_ref,
          notes: form.notes,
        });
        toast.success(`${tool.tl_number} issued (stock now ${res.data.new_stock})`);
      } else {
        if (!form.condition) {
          toast.error('Select condition');
          setSaving(false);
          return;
        }
        const res = await tsReturnTool(tool.id, {
          condition: form.condition,
          hits_used_range: form.hits_used_range,
          length_after_use_mm: form.length_after_use_mm ? parseFloat(form.length_after_use_mm) : null,
          photo_url: form.photo_url,
          root_cause: form.root_cause,
          notes: form.notes,
        });
        toast.success(`${tool.tl_number} returned → ${res.data.new_status.replace('_', ' ')}`);
      }
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            {isIssue ? <><ArrowUpRight size={14} className="text-amber-600" /> Issue {tool.tl_number}</>
              : <><ArrowDownRight size={14} className="text-green-600" /> Return {tool.tl_number}</>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="text-xs text-gray-500">{tool.description}</div>
          {isIssue ? (
            <>
              <div>
                <Label className="text-xs">Machine</Label>
                <Select value={form.machine} onValueChange={(v) => setForm((p) => ({ ...p, machine: v }))}>
                  <SelectTrigger className="h-9 text-xs" data-testid="issue-machine"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {machines.map((m) => <SelectItem key={m.code} value={m.code}>{m.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Operator</Label>
                <Input autoFocus value={form.operator || ''} onChange={(e) => setForm((p) => ({ ...p, operator: e.target.value }))} className="h-9 text-xs" data-testid="issue-operator" />
              </div>
              <div>
                <Label className="text-xs">Job Card Ref</Label>
                <Input value={form.job_card_ref || ''} onChange={(e) => setForm((p) => ({ ...p, job_card_ref: e.target.value }))} className="h-9 text-xs" />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">Condition *</Label>
                <Select value={form.condition} onValueChange={(v) => setForm((p) => ({ ...p, condition: v }))}>
                  <SelectTrigger className="h-9 text-xs" data-testid="return-condition"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good — back to stock</SelectItem>
                    <SelectItem value="worn">Worn — back to stock</SelectItem>
                    <SelectItem value="needs_regrind">Needs Regrind — send to tool room</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="broken">Broken</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Hits used</Label>
                  <Select value={form.hits_used_range} onValueChange={(v) => setForm((p) => ({ ...p, hits_used_range: v }))}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Range…" /></SelectTrigger>
                    <SelectContent>
                      {['<5K', '5-10K', '10-20K', '20-50K', '>50K', 'Unknown'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Length after use (mm)</Label>
                  <Input type="number" step="0.1" value={form.length_after_use_mm || ''} onChange={(e) => setForm((p) => ({ ...p, length_after_use_mm: e.target.value }))} className="h-9 text-xs" />
                </div>
              </div>
              {['damaged', 'broken'].includes(form.condition) && (
                <>
                  <div>
                    <Label className="text-xs">Photo URL *</Label>
                    <Input value={form.photo_url || ''} onChange={(e) => setForm((p) => ({ ...p, photo_url: e.target.value }))} placeholder="Paste image URL" className="h-9 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Root Cause</Label>
                    <Select value={form.root_cause} onValueChange={(v) => setForm((p) => ({ ...p, root_cause: v }))}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>
                        {['Fatigue', 'Impact', 'Setup Error', 'Material Defect', 'Operator Error', 'Other'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </>
          )}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes || ''} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} className="text-xs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} style={{ backgroundColor: isIssue ? '#d97706' : '#0e7a45' }} data-testid={isIssue ? 'submit-issue' : 'submit-return'}>
            {saving ? 'Saving…' : (isIssue ? 'Issue Tool' : 'Return Tool')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ Machine Master mini-panel ============
function MasterPanel({ machines, vendors, onReload }) {
  const [showMachine, setShowMachine] = useState(false);
  const [showVendor, setShowVendor] = useState(false);
  const [mForm, setMForm] = useState({});
  const [vForm, setVForm] = useState({});

  const saveMachine = async () => {
    try {
      await tsCreateMachine({ code: mForm.code, name: mForm.name, machine_type: mForm.type, station_count: mForm.stations ? parseInt(mForm.stations) : null });
      toast.success(`Machine ${mForm.code} added`);
      setShowMachine(false);
      setMForm({});
      onReload?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const saveVendor = async () => {
    try {
      await tsCreateVendor({ name: vForm.name, country: vForm.country });
      toast.success(`Vendor added`);
      setShowVendor(false);
      setVForm({});
      onReload?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Factory size={16} className="text-[#1a2fce]" /> Masters
          <Badge variant="outline" className="ml-1 text-[10px]">{machines.length} machines · {vendors.length} vendors</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-medium text-gray-600">Machines</div>
            <Button size="sm" variant="ghost" onClick={() => setShowMachine(true)} className="h-6 text-[11px]" data-testid="add-machine-btn"><Plus size={12} /> Add</Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {machines.length === 0 ? <span className="text-[10px] text-gray-400 italic">None — add your first machine (e.g. CBF01)</span>
              : machines.map((m) => <Badge key={m.code} variant="outline" className="font-mono text-[10px]">{m.code}</Badge>)}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-medium text-gray-600">Vendors</div>
            <Button size="sm" variant="ghost" onClick={() => setShowVendor(true)} className="h-6 text-[11px]" data-testid="add-vendor-btn"><Plus size={12} /> Add</Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {vendors.length === 0 ? <span className="text-[10px] text-gray-400 italic">None</span>
              : vendors.map((v) => <Badge key={v.id} variant="outline" className="text-[10px]">{v.name}</Badge>)}
          </div>
        </div>
      </CardContent>

      <Dialog open={showMachine} onOpenChange={setShowMachine}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Add Machine</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label className="text-xs">Code *</Label><Input value={mForm.code || ''} onChange={(e) => setMForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="CBF01" className="h-9 text-xs font-mono" data-testid="new-machine-code" /></div>
            <div><Label className="text-xs">Name *</Label><Input value={mForm.name || ''} onChange={(e) => setMForm((p) => ({ ...p, name: e.target.value }))} placeholder="Cold Bolt Former 1" className="h-9 text-xs" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Type</Label>
                <Select value={mForm.type} onValueChange={(v) => setMForm((p) => ({ ...p, type: v }))}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bolt_former">Bolt Former</SelectItem>
                    <SelectItem value="nut_former">Nut Former</SelectItem>
                    <SelectItem value="press">Press</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Stations</Label><Input type="number" value={mForm.stations || ''} onChange={(e) => setMForm((p) => ({ ...p, stations: e.target.value }))} className="h-9 text-xs" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMachine(false)}>Cancel</Button>
            <Button onClick={saveMachine} disabled={!mForm.code || !mForm.name} style={{ backgroundColor: '#1a2fce' }} data-testid="save-machine">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showVendor} onOpenChange={setShowVendor}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">Add Vendor</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label className="text-xs">Name *</Label><Input value={vForm.name || ''} onChange={(e) => setVForm((p) => ({ ...p, name: e.target.value }))} className="h-9 text-xs" data-testid="new-vendor-name" /></div>
            <div><Label className="text-xs">Country</Label><Input value={vForm.country || ''} onChange={(e) => setVForm((p) => ({ ...p, country: e.target.value }))} className="h-9 text-xs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendor(false)}>Cancel</Button>
            <Button onClick={saveVendor} disabled={!vForm.name} style={{ backgroundColor: '#1a2fce' }} data-testid="save-vendor">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============ Main Page ============
export default function ToolStorePage() {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [machines, setMachines] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  const [regDialog, setRegDialog] = useState(false);
  const [quickDialog, setQuickDialog] = useState(false);
  const [txnTool, setTxnTool] = useState(null);
  const [txnMode, setTxnMode] = useState('issue');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes, mRes, vRes] = await Promise.all([
        tsListTools({
          q: q || undefined,
          status: filterStatus || undefined,
          category: filterCategory || undefined,
          limit: 100,
        }),
        tsStats(),
        tsListMachines(),
        tsListVendors(),
      ]);
      setTools(tRes.data.tools || []);
      setTotal(tRes.data.total || 0);
      setStats(sRes.data);
      setMachines(mRes.data || []);
      setVendors(vRes.data || []);
    } catch (err) {
      toast.error('Failed to load Tool Store');
    }
    setLoading(false);
  }, [q, filterStatus, filterCategory]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Bluetooth HID scanner support (PRD §2.3): autofocus + Enter submits
  useEffect(() => {
    const t = setTimeout(() => searchRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, []);

  const statCards = stats ? [
    { label: 'Total Tools', value: stats.total_tools, color: '#1a2fce', icon: Wrench },
    { label: 'In Store', value: stats.in_store, color: '#0e7a45', icon: Store },
    { label: 'Issued', value: stats.issued, color: '#d97706', icon: ArrowUpRight },
    { label: 'In Tool Room', value: stats.in_tool_room, color: '#1a56db', icon: Factory },
    { label: 'Pending Scrap', value: stats.pending_scrap, color: '#dc2626', icon: AlertTriangle },
    { label: 'Quick-Reg Pending', value: stats.quick_register_pending, color: '#ea580c', icon: Zap },
  ] : [];

  return (
    <div className="space-y-4" data-testid="toolstore-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Wrench size={20} className="text-[#1a2fce]" /> Tool Store
        </h1>
        <Badge variant="outline" className="text-[10px]">Phase 2A · Foundation</Badge>
        <div className="flex-1" />
        <Button onClick={() => setQuickDialog(true)} className="h-9 gap-1 text-xs" variant="outline" style={{ borderColor: '#ea580c', color: '#ea580c' }} data-testid="open-quick-register">
          <Zap size={14} /> Quick Register
        </Button>
        <Button onClick={() => setRegDialog(true)} className="h-9 gap-1 text-xs" style={{ backgroundColor: '#1a2fce' }} data-testid="open-full-register">
          <Plus size={14} /> Register Tool
        </Button>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.label} className="border-0 shadow-sm relative overflow-hidden" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: s.color }} />
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase">{s.label}</div>
                      <div className="text-lg font-bold mt-0.5" style={{ color: s.color }}>{s.value}</div>
                    </div>
                    <Icon size={14} style={{ color: s.color }} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Low stock alerts */}
      {stats && stats.low_stock_count > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-red-500" data-testid="low-stock-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertTriangle size={14} /> {stats.low_stock_count} tool(s) below reorder level
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {stats.low_stock.slice(0, 10).map((t) => (
                <Badge key={t.id} variant="outline" className="text-[10px] gap-1 border-red-300">
                  <span className="font-mono">{t.tl_number}</span>
                  <span className="text-red-600">{t.current_stock}/{t.min_level}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {/* Left: Masters */}
        <div className="lg:col-span-1 space-y-3">
          <MasterPanel machines={machines} vendors={vendors} onReload={loadAll} />
        </div>

        {/* Right: Tool list */}
        <div className="lg:col-span-3 space-y-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3">
              {/* Search + filter */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 relative">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                  <Input
                    ref={searchRef}
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Scan or type TL-XXXX, description, engraved no., bin…"
                    className="h-9 text-sm pl-8"
                    data-testid="tool-search-input"
                  />
                </div>
                <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === '__all__' ? '' : v)}>
                  <SelectTrigger className="w-32 h-9 text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All statuses</SelectItem>
                    <SelectItem value="in_store">In Store</SelectItem>
                    <SelectItem value="issued">Issued</SelectItem>
                    <SelectItem value="in_tool_room">In Tool Room</SelectItem>
                    <SelectItem value="pending_scrap">Pending Scrap</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterCategory} onValueChange={(v) => setFilterCategory(v === '__all__' ? '' : v)}>
                  <SelectTrigger className="w-32 h-9 text-xs" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All categories</SelectItem>
                    {CATEGORIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={loadAll} disabled={loading} className="h-9 px-2" data-testid="reload-tools">
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                </Button>
              </div>

              {/* Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[60vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="text-left text-[10px] uppercase text-gray-500">
                        <th className="px-2 py-2">TL#</th>
                        <th className="px-2 py-2">Description</th>
                        <th className="px-2 py-2">Cat</th>
                        <th className="px-2 py-2">Machine</th>
                        <th className="px-2 py-2">Bin</th>
                        <th className="px-2 py-2 text-right">Stock</th>
                        <th className="px-2 py-2">Status</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tools.length === 0 ? (
                        <tr><td colSpan={8} className="text-center text-gray-400 py-6 text-xs">
                          {loading ? 'Loading…' : 'No tools yet — click Register Tool or Quick Register to add your first one'}
                        </td></tr>
                      ) : tools.map((t) => {
                        const cat = CAT_MAP[t.category] || CAT_MAP.other;
                        return (
                          <tr key={t.id} className="border-t hover:bg-gray-50" data-testid={`tool-row-${t.tl_number}`}>
                            <td className="px-2 py-1.5 font-mono font-bold text-[#0d1f6e]">{t.tl_number}</td>
                            <td className="px-2 py-1.5">
                              <div className="truncate max-w-[220px]">{t.description}</div>
                              {t.functional_id && <div className="text-[10px] text-gray-400 font-mono truncate max-w-[220px]">{t.functional_id}</div>}
                            </td>
                            <td className="px-2 py-1.5">
                              <Badge variant="outline" className="text-[10px]" style={{ borderColor: cat.color, color: cat.color }}>
                                {cat.label}
                              </Badge>
                            </td>
                            <td className="px-2 py-1.5 font-mono text-[10px]">{(t.machine_compatibility || []).join(', ')}</td>
                            <td className="px-2 py-1.5 font-mono text-[10px] text-gray-500 truncate max-w-[120px]">{t.bin_location || '—'}</td>
                            <td className="px-2 py-1.5 text-right">
                              <span className={`font-bold ${(t.min_level && t.current_stock < t.min_level) ? 'text-red-600' : ''}`}>
                                {t.current_stock}
                              </span>
                              {t.min_level ? <span className="text-gray-400 text-[10px]"> /{t.min_level}</span> : null}
                            </td>
                            <td className="px-2 py-1.5">
                              <Badge variant="outline" className="text-[10px]" style={{ borderColor: STATUS_COLORS[t.status], color: STATUS_COLORS[t.status] }}>
                                {t.status.replace('_', ' ')}
                              </Badge>
                              {t.quick_register && !t.quick_register_completed && (
                                <div className="text-[9px] text-orange-600 mt-0.5 flex items-center gap-0.5">
                                  <Zap size={9} /> quick-reg
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1">
                                {t.status === 'in_store' && t.current_stock > 0 && (
                                  <Button size="sm" variant="ghost" onClick={() => { setTxnTool(t); setTxnMode('issue'); }} className="h-6 px-2 text-[10px] text-amber-600" data-testid={`issue-${t.tl_number}`}>
                                    Issue
                                  </Button>
                                )}
                                {['issued', 'in_store'].includes(t.status) && (
                                  <Button size="sm" variant="ghost" onClick={() => { setTxnTool(t); setTxnMode('return'); }} className="h-6 px-2 text-[10px] text-green-700" data-testid={`return-${t.tl_number}`}>
                                    Return
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => navigate(`/tool-store/${t.id}`)} className="h-6 px-1">
                                  <ChevronRight size={12} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {tools.length > 0 && (
                <div className="text-[10px] text-gray-400 text-right mt-1">Showing {tools.length} of {total}</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Register dialogs */}
      <RegisterToolDialog open={regDialog} onOpenChange={setRegDialog} machines={machines} vendors={vendors} onCreated={loadAll} mode="full" />
      <RegisterToolDialog open={quickDialog} onOpenChange={setQuickDialog} machines={machines} vendors={vendors} onCreated={loadAll} mode="quick" />
      <IssueReturnDialog open={!!txnTool} onOpenChange={(o) => !o && setTxnTool(null)} tool={txnTool} mode={txnMode} machines={machines} onDone={loadAll} />
    </div>
  );
}
